import { useCallback, useEffect, useRef } from 'react';
import type Konva from 'konva';
import type { Point } from '../lib/stageInteractions';
import { computeWheelZoom } from '../lib/stageInteractions';
import { SessionMetrics } from '../../../domains/analytics/services/SessionMetrics';

export function useCanvasPanZoomHandlers(input: {
  stageRef: { current: Konva.Stage | null };
  clearActiveComment: () => void;

  isPanMode: boolean;
  isPanning: boolean;
  setIsPanning: (next: boolean) => void;
  panStart: { x: number; y: number };
  setPanStart: (next: { x: number; y: number }) => void;
  panOverlayActiveRef: { current: boolean };
  previewViewportStage: (next: { scale: number; offsetX: number; offsetY: number }) => void;
  getLatestViewportStage: () => { scale: number; offsetX: number; offsetY: number };
  commitViewportStageDebounced: (next: { scale: number; offsetX: number; offsetY: number }) => void;
  commitViewportStageImmediate: (next: { scale: number; offsetX: number; offsetY: number }) => void;
}): {
  handleWheel: (e: Konva.KonvaEventObject<WheelEvent>) => void;
  handlePanOverlayWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
  handlePanOverlayMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  handlePanOverlayMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  handlePanOverlayMouseUp: () => void;
} {
  const {
    stageRef,
    clearActiveComment,
    isPanMode,
    isPanning,
    setIsPanning,
    panStart,
    setPanStart,
    panOverlayActiveRef,
    previewViewportStage,
    getLatestViewportStage,
    commitViewportStageDebounced,
    commitViewportStageImmediate,
  } = input;
  const viewportMetricTimerRef = useRef<number | null>(null);
  const pendingViewportMetricRef = useRef<{
    name: 'pan' | 'zoom';
    properties?: Record<string, unknown>;
  } | null>(null);
  const scheduleViewportMetric = useCallback((
    name: 'pan' | 'zoom',
    properties?: Record<string, unknown>,
  ) => {
    pendingViewportMetricRef.current = { name, properties };
    if (viewportMetricTimerRef.current !== null) {
      window.clearTimeout(viewportMetricTimerRef.current);
    }
    viewportMetricTimerRef.current = window.setTimeout(() => {
      viewportMetricTimerRef.current = null;
      const pending = pendingViewportMetricRef.current;
      pendingViewportMetricRef.current = null;
      if (pending) SessionMetrics.sampled(pending.name, pending.properties);
    }, 200);
  }, []);

  useEffect(() => () => {
    if (viewportMetricTimerRef.current !== null) {
      window.clearTimeout(viewportMetricTimerRef.current);
    }
  }, []);

  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      // Prevent the browser from performing page scroll/zoom
      e.evt.preventDefault();
      const native = e.evt;
      const stage = stageRef.current;
      if (!stage) return;

      if (e.target?.attrs?.name !== 'comment-pin') {
        clearActiveComment();
      }

      // Determine whether this wheel event is intended as zoom (pinch/ctrl+wheel) or pan (two-finger scroll)
      const isZoomGesture = !!(native.ctrlKey || native.metaKey);
      const latestViewport = getLatestViewportStage();

      // Derive pointer position reliably even if Konva hasn't updated it yet (e.g., trackpad scroll without prior mousemove)
      let pointer = stage.getPointerPosition();
      if (!pointer) {
        try {
          const rect = stage.container().getBoundingClientRect();
          pointer = { x: native.clientX - rect.left, y: native.clientY - rect.top } as Point;
        } catch {
          // Fallback to stage center if we still cannot resolve pointer
          pointer = { x: stage.width() / 2, y: stage.height() / 2 } as Point;
        }
      }

      if (isZoomGesture) {
        const { scale, position } = computeWheelZoom(
          latestViewport.scale,
          pointer as Point,
          { x: latestViewport.offsetX, y: latestViewport.offsetY },
          native.deltaY
        );
        const next = { scale, offsetX: position.x, offsetY: position.y };
        previewViewportStage(next);
        commitViewportStageDebounced(next);
        scheduleViewportMetric('zoom', { level: scale });
        return;
      }

      // Default: treat wheel as panning using deltaX/deltaY (trackpad two-finger scroll)
      const nextX = latestViewport.offsetX - (native.deltaX || 0);
      const nextY = latestViewport.offsetY - (native.deltaY || 0);
      const next = { scale: latestViewport.scale, offsetX: nextX, offsetY: nextY };
      previewViewportStage(next);
      commitViewportStageDebounced(next);
      scheduleViewportMetric('pan');
    },
    [
      clearActiveComment,
      commitViewportStageDebounced,
      getLatestViewportStage,
      previewViewportStage,
      scheduleViewportMetric,
      stageRef,
    ],
  );

  const handlePanOverlayWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const stage = stageRef.current;
      if (!stage) return;

      const isZoomGesture = !!(e.ctrlKey || e.metaKey);
      const latestViewport = getLatestViewportStage();
      let pointer = stage.getPointerPosition();
      if (!pointer) {
        // derive from overlay coords
        try {
          const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
          pointer = { x: e.clientX - rect.left, y: e.clientY - rect.top } as Point;
        } catch {
          pointer = { x: stage.width() / 2, y: stage.height() / 2 } as Point;
        }
      }

      if (isZoomGesture) {
        const { scale, position } = computeWheelZoom(
          latestViewport.scale,
          pointer as Point,
          { x: latestViewport.offsetX, y: latestViewport.offsetY },
          e.deltaY
        );
        const next = { scale, offsetX: position.x, offsetY: position.y };
        previewViewportStage(next);
        commitViewportStageDebounced(next);
        return;
      }

      const nextX = latestViewport.offsetX - (e.deltaX || 0);
      const nextY = latestViewport.offsetY - (e.deltaY || 0);
      const next = { scale: latestViewport.scale, offsetX: nextX, offsetY: nextY };
      previewViewportStage(next);
      commitViewportStageDebounced(next);
    },
    [
      commitViewportStageDebounced,
      getLatestViewportStage,
      previewViewportStage,
      stageRef,
    ],
  );

  const handlePanOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanMode) return;
      const stage = stageRef.current;
      if (!stage) return;
      e.preventDefault();
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const pos = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      setIsPanning(true);
      panOverlayActiveRef.current = true;
      const latestViewport = getLatestViewportStage();
      setPanStart({
        x: pos.x - latestViewport.offsetX,
        y: pos.y - latestViewport.offsetY,
      });
      document.body.style.cursor = 'grabbing';
    },
    [
      getLatestViewportStage,
      isPanMode,
      panOverlayActiveRef,
      setIsPanning,
      setPanStart,
      stageRef,
    ],
  );

  const handlePanOverlayMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanMode || !isPanning) return;
      const stage = stageRef.current;
      if (!stage) return;
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const pos = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const nextX = pos.x - panStart.x;
      const nextY = pos.y - panStart.y;
      previewViewportStage({ scale: stage.scaleX(), offsetX: nextX, offsetY: nextY });
    },
    [isPanMode, isPanning, panStart.x, panStart.y, previewViewportStage, stageRef],
  );

  const handlePanOverlayMouseUp = useCallback(() => {
    if (!isPanMode) return;
    if (isPanning) {
      setIsPanning(false);
      panOverlayActiveRef.current = false;
      document.body.style.cursor = 'grab';
      const stage = stageRef.current;
      if (stage) {
        commitViewportStageImmediate(getLatestViewportStage());
      }
    }
  }, [
    commitViewportStageImmediate,
    getLatestViewportStage,
    isPanMode,
    isPanning,
    panOverlayActiveRef,
    setIsPanning,
    stageRef,
  ]);

  return {
    handleWheel,
    handlePanOverlayWheel,
    handlePanOverlayMouseDown,
    handlePanOverlayMouseMove,
    handlePanOverlayMouseUp,
  };
}
